import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PencilLine } from 'lucide-react';
import { z } from 'zod';
import {
  FormDateField,
  FormSearchSelectField,
  FormSelectField,
  FormTextareaField,
  FormTextField,
} from '@/components/common/FormDialog';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import {
  DONATION_COMMENT_MAX_LENGTH,
  PAYMENT_PLACE_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
} from '../constants/marketPromotion.constants';
import { useAmendDonation } from '../hooks/useMpMutations';
import { useSourceInstituteOptions } from '../hooks/useSourceInstituteOptions';
import type {
  AmendDonationPayload,
  MpApprovalDetail,
  MpApprovalRow,
} from '../types/marketPromotion.types';
import {
  findOptionId,
  findOptionName,
  isPicked,
  isTransactionRefNeeded,
  OPTIONAL_WHOLE_NUMBER_PATTERN,
  toDateFieldText,
  toNumberText,
  toOptionalText,
  WHOLE_NUMBER_PATTERN,
} from '../utils/donationFormValues';
import { DonationFormCard } from './DonationFormCard';

const donationEditFormSchema = z.object({
  sourceOfFund: z.number().optional(),
  approvedAmount: z.string().trim().regex(WHOLE_NUMBER_PATTERN, 'Enter the amount in taka'),
  paymentTypeId: z.number().optional().refine(isPicked, {
    message: 'Pick the payment type',
  }),
  transactionRef: z.string(),
  approxSaleQty: z.string().trim().regex(OPTIONAL_WHOLE_NUMBER_PATTERN, 'Enter a whole number'),
  paymentPlaceId: z.number().optional().refine(isPicked, {
    message: 'Pick where it was paid',
  }),
  witness: z.string(),
  givenDate: z.string(),
  totalStudent: z.string().trim().regex(OPTIONAL_WHOLE_NUMBER_PATTERN, 'Enter a whole number'),
  recommendedComments: z.string().max(DONATION_COMMENT_MAX_LENGTH),
  approvalComments: z.string().max(DONATION_COMMENT_MAX_LENGTH),
});

type DonationEditFormValues = z.infer<typeof donationEditFormSchema>;

const toOptionalNumber = (text: string): number | undefined =>
  text.trim() === '' ? undefined : Number(text);

function getSavedEditValues(donation: MpApprovalRow): DonationEditFormValues {
  return {
    sourceOfFund: donation.sourceOfFund ?? undefined,
    approvedAmount: String(donation.approvedAmount),
    paymentTypeId: findOptionId(PAYMENT_TYPE_OPTIONS, donation.paymentType),
    transactionRef: donation.transactionRef ?? '',
    approxSaleQty: toNumberText(donation.approxSaleQty),
    paymentPlaceId: findOptionId(PAYMENT_PLACE_OPTIONS, donation.paymentPlace),
    witness: donation.witness ?? '',
    givenDate: toDateFieldText(donation.givenDate),
    totalStudent: toNumberText(donation.totalStudent),
    recommendedComments: donation.recommendedComments ?? '',
    approvalComments: donation.approvalComments ?? '',
  };
}

function withValue<TKey extends keyof AmendDonationPayload>(
  key: TKey,
  fieldValue: AmendDonationPayload[TKey] | undefined,
): Partial<AmendDonationPayload> {
  return fieldValue === undefined ? {} : { [key]: fieldValue };
}

function toAmendPayload(values: DonationEditFormValues): AmendDonationPayload {
  return {
    approvedAmount: Number(values.approvedAmount),
    ...withValue('sourceOfFund', values.sourceOfFund),
    ...withValue('paymentType', findOptionName(PAYMENT_TYPE_OPTIONS, values.paymentTypeId)),
    ...withValue('transactionRef', toOptionalText(values.transactionRef)),
    ...withValue('approxSaleQty', toOptionalNumber(values.approxSaleQty)),
    ...withValue('paymentPlace', findOptionName(PAYMENT_PLACE_OPTIONS, values.paymentPlaceId)),
    ...withValue('witness', toOptionalText(values.witness)),
    ...withValue('givenDate', toOptionalText(values.givenDate)),
    ...withValue('totalStudent', toOptionalNumber(values.totalStudent)),
    ...withValue('recommendedComments', toOptionalText(values.recommendedComments)),
    ...withValue('approvalComments', toOptionalText(values.approvalComments)),
  };
}

interface DonationEditFormProps {
  donation: MpApprovalDetail;
  onClose: () => void;
}

export function DonationEditForm({ donation, onClose }: DonationEditFormProps) {
  const form = useForm<DonationEditFormValues>({
    resolver: zodResolver(donationEditFormSchema),
    defaultValues: getSavedEditValues(donation),
  });
  const amendMutation = useAmendDonation(onClose);
  const sourceInstitutes = useSourceInstituteOptions(donation);
  const paymentTypeId = useWatch({ control: form.control, name: 'paymentTypeId' });
  const needsTransactionRef = isTransactionRefNeeded(paymentTypeId);

  function handleSave(values: DonationEditFormValues) {
    amendMutation.mutate({ detailId: donation.detailId, payload: toAmendPayload(values) });
  }

  return (
    <DonationFormCard
      donation={donation}
      title="Edit Donation"
      icon={PencilLine}
      form={form}
      onSubmit={handleSave}
      onClose={onClose}
      submitLabel="Save"
      isSubmitPending={amendMutation.isPending}
      paymentRows={
        <>
          <FormSearchSelectField
            control={form.control}
            name="sourceOfFund"
            label="Source of Fund"
            options={sourceInstitutes.options}
            savedOption={sourceInstitutes.savedOption}
            onSearchChange={sourceInstitutes.onSearchChange}
            isSearching={sourceInstitutes.isSearching}
            placeholder="Pick the institute the fund came from"
            searchPlaceholder="Type 3 letters of a name, EIIN or number"
            emptyText="Type at least 3 letters to find an institute"
          />
          <FormTextField
            control={form.control}
            name="approvedAmount"
            label="Approved Amount"
            inputMode="numeric"
            leadingSymbol={TAKA_SYMBOL}
            hasTabularNumbers
          />
          <FormSelectField
            control={form.control}
            name="paymentTypeId"
            label="Payment Type"
            options={PAYMENT_TYPE_OPTIONS}
            isLoading={false}
            placeholder="Pick the payment type"
          />
          {needsTransactionRef ? (
            <FormTextField
              control={form.control}
              name="transactionRef"
              label="Transaction Ref"
              hasTabularNumbers
            />
          ) : null}
          <FormSelectField
            control={form.control}
            name="paymentPlaceId"
            label="Payment Place"
            options={PAYMENT_PLACE_OPTIONS}
            isLoading={false}
            placeholder="Pick where it was paid"
          />
          <FormTextField
            control={form.control}
            name="approxSaleQty"
            label="Approx Sale"
            inputMode="numeric"
            hasTabularNumbers
          />
          <FormTextField
            control={form.control}
            name="totalStudent"
            label="Total Students"
            inputMode="numeric"
            hasTabularNumbers
          />
          <FormTextField control={form.control} name="witness" label="Witness" />
          <FormDateField control={form.control} name="givenDate" label="Given Date" />
        </>
      }
      decisionRows={
        <>
          <FormTextareaField
            control={form.control}
            name="recommendedComments"
            label="Recommendation Comment"
            maxLength={DONATION_COMMENT_MAX_LENGTH}
          />
          <FormTextareaField
            control={form.control}
            name="approvalComments"
            label="Approval Comment"
            maxLength={DONATION_COMMENT_MAX_LENGTH}
          />
        </>
      }
    />
  );
}
